import { useState, useEffect } from 'react';
import { commentAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { VoteButtons } from './VoteButtons';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Trash2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CommentSection = ({ targetType, targetId, currentUser }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [targetType, targetId]);

  const fetchComments = async () => {
    try {
      const response = await commentAPI.getAll(targetType, targetId);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await commentAPI.create(targetType, targetId, { content: newComment });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please sign in to comment');
      } else {
        toast.error('Failed to add comment');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentAPI.delete(commentId);
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comment-section">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-slate-600" />
        <h3 className="text-lg font-semibold text-slate-900">
          Comments ({comments.length})
        </h3>
      </div>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="comment-form">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          className="min-h-[100px] resize-none"
          data-testid="comment-input"
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isSubmitting || !newComment.trim()}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="submit-comment-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              'Post Comment'
            )}
          </Button>
        </div>
      </form>

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-center text-slate-500 py-8">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.comment_id}
              className="flex gap-3 p-4 bg-slate-50 rounded-lg"
              data-testid={`comment-${comment.comment_id}`}
            >
              <VoteButtons
                targetType="comment"
                targetId={comment.comment_id}
                initialUpvotes={comment.upvotes}
                initialDownvotes={comment.downvotes}
                userVote={comment.user_vote}
                size="sm"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Link to={`/users/${comment.author_id}`} className="flex items-center gap-2 hover:opacity-80">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={comment.author_picture} />
                      <AvatarFallback className="text-xs bg-indigo-100 text-indigo-700">
                        {comment.author_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-slate-900">{comment.author_name}</span>
                  </Link>
                  <span className="text-xs text-slate-500">
                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                  </span>
                </div>
                
                <p className="text-slate-700 whitespace-pre-wrap">{comment.content}</p>
                
                {currentUser?.user_id === comment.author_id && (
                  <button
                    onClick={() => handleDelete(comment.comment_id)}
                    className="mt-2 flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                    data-testid={`delete-comment-${comment.comment_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
